/**
 * A filter that takes several values: a button that opens a list of checkboxes.
 *
 * `Select` answers "which one?"; a filter often needs "which of these?", and
 * the native `<select multiple>` is a list box nobody can operate on a phone
 * or find on a toolbar. This is a disclosure instead: the button says what is
 * chosen, the panel holds real checkboxes (so a screen reader, the keyboard
 * and a touch screen all work with no extra code), and a search box appears
 * when the list is long enough that scrolling it is the slow way.
 *
 * Several chosen values are alternatives — "either of these" — and an empty
 * choice means "no filter", the same as `Select`'s empty option. What the
 * values *do* is the caller's: this only holds the choice.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";

import "./controls.css";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  /** Announced as the control's name, and shown on the button when nothing is chosen. */
  label: string;
  options: readonly MultiSelectOption[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  /** Said on the button when nothing is chosen, e.g. "All brands". Defaults to `label`. */
  allLabel?: string;
  /** The button's text for several choices; `{{count}}` is the number chosen. */
  countLabel?: (count: number) => string;
  /** Clears the choice. */
  clearLabel: string;
  /** The search box's placeholder, shown when the list is long. */
  searchLabel?: string;
  /** From how many options the search box appears. */
  searchFrom?: number;
  disabled?: boolean;
  className?: string;
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  allLabel,
  countLabel,
  clearLabel,
  searchLabel,
  searchFrom = 10,
  disabled = false,
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const root = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Closed by a click anywhere else, and by Escape — a panel that stays open
  // over the table it filters is in the way of the thing it is for.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const chosen = new Set(selected);
  const labelOf = useMemo(() => new Map(options.map((o) => [o.value, o.label])), [options]);
  const needle = term.trim().toLocaleLowerCase();
  const shown = needle
    ? options.filter((option) => option.label.toLocaleLowerCase().includes(needle))
    : options;

  // What the button says. One choice is named — "Casablanca" tells more than
  // "1 selected" — several are counted.
  let summary = allLabel ?? label;
  if (selected.length === 1) summary = labelOf.get(selected[0] ?? "") ?? selected[0] ?? summary;
  else if (selected.length > 1)
    summary = countLabel ? countLabel(selected.length) : `${selected.length}`;

  function toggle(value: string) {
    onChange(chosen.has(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  }

  return (
    <div
      ref={root}
      className={["hardauto-multiselect", className].filter(Boolean).join(" ")}
      data-active={selected.length > 0 ? "true" : undefined}
    >
      <button
        type="button"
        className="hardauto-input hardauto-select hardauto-multiselect__button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`${label}: ${summary}`}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="hardauto-multiselect__summary">{summary}</span>
      </button>

      {open ? (
        <div className="hardauto-multiselect__panel" id={panelId} role="group" aria-label={label}>
          {options.length >= searchFrom ? (
            <input
              type="search"
              className="hardauto-input hardauto-multiselect__search"
              placeholder={searchLabel ?? ""}
              aria-label={searchLabel ?? label}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              // Enter in the search box must not submit a form around the filter.
              onKeyDown={(event) => {
                if (event.key === "Enter") event.preventDefault();
              }}
            />
          ) : null}
          <ul className="hardauto-multiselect__list">
            {shown.map((option) => (
              <li key={option.value}>
                <label className="hardauto-checkbox">
                  <input
                    type="checkbox"
                    checked={chosen.has(option.value)}
                    onChange={() => toggle(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              </li>
            ))}
          </ul>
          {selected.length > 0 ? (
            <button
              type="button"
              className="hardauto-multiselect__clear"
              onClick={() => onChange([])}
            >
              {clearLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
